var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import styled from 'styled-components';
import { deprecated } from '@ally/metronome-icons';
import { Box } from '../Box';
import { Dejargonator } from '../Dejargonator';
import { ErrorNotification } from '../ErrorNotification';
import { Icon } from '../Icon';
import { ScreenReaderOnly } from '../ScreenReaderOnly';
import { Space } from '../Space';
import { TextBodyDEPRECATED } from '../TypographyDEPRECATED';
import { isMetronomeRadioButton } from './utils';
import { a11y } from './strings';
const StyledFieldset = styled.fieldset `
  border: none;
  margin: 0px;
  padding: 0px;
  label:last-child {
    margin-bottom: 0;
  }
`;
const StyledTextBody = styled(TextBodyDEPRECATED) `
  line-height: ${({ theme: { font } }) => font.body.xs.lineHeight};
  text-transform: none;
`;
export const RadioGroup = React.forwardRef((_a, ref) => {
    var { name, legendText, isLegendHidden, value, defaultValue, disableDefaultSelection, children = [], className, altText, onChange, onBlur, errorMessage, contextualHelp } = _a, others = __rest(_a, ["name", "legendText", "isLegendHidden", "value", "defaultValue", "disableDefaultSelection", "children", "className", "altText", "onChange", "onBlur", "errorMessage", "contextualHelp"]);
    const { current: legendId } = React.useRef(`radio-group-legend${uuidv4()}`);
    const { current: errorId } = React.useRef(`radio-group-error-${uuidv4()}`);
    const [selectedValue, setSelectedValue] = useState(disableDefaultSelection ? undefined : defaultValue);
    useEffect(() => {
        var _a, _b;
        if (disableDefaultSelection || value) {
            return;
        }
        const firstValue = (_b = (_a = children[0]) === null || _a === void 0 ? void 0 : _a.props) === null || _b === void 0 ? void 0 : _b.value;
        if (!defaultValue) {
            setSelectedValue(firstValue);
        }
        onChange === null || onChange === void 0 ? void 0 : onChange(defaultValue || firstValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const changeHandler = (event) => {
        const val = event.currentTarget.value;
        if (!value)
            setSelectedValue(val);
        onChange === null || onChange === void 0 ? void 0 : onChange(val, event);
    };
    return (React.createElement("div", Object.assign({}, others),
        React.createElement(StyledFieldset, { className: className, "aria-labelledby": legendId, "aria-describedby": errorMessage ? errorId : undefined, role: "group", ref: ref },
            isLegendHidden ? (React.createElement(ScreenReaderOnly, { id: legendId, as: "legend" }, legendText)) : (React.createElement(Box, { display: "flex", alignItems: "center", m: "none", p: "none", mb: "md" },
                React.createElement(Space, { mr: "xxs" },
                    React.createElement(StyledTextBody, { id: legendId, tag: "legend", size: "xs", weight: "bold" }, legendText)),
                !!contextualHelp && (React.createElement(Dejargonator, { trigger: Object.assign({ variant: 'icon', content: (React.createElement(Icon, { icon: deprecated.muiHelpOutline, size: "md" })) }, ('topic' in contextualHelp
                        ? { topic: contextualHelp.topic }
                        : { ariaLabel: contextualHelp.ariaLabelTrigger })), heading: contextualHelp.heading, headingTag: contextualHelp.headingTag, body: contextualHelp.body, ariaLabelPopover: contextualHelp.ariaLabelPopover || a11y.ariaLabelPopover })))),
            altText,
            React.Children.map(children, (child, index) => {
                if (
                // type checks are redundant, but make TS happy :)
                typeof child === 'string' ||
                    typeof child === 'number' ||
                    !isMetronomeRadioButton(child)) {
                    return child;
                }
                const selectedOption = value || selectedValue;
                const { value: childValue } = child.props || {};
                return React.cloneElement(child, {
                    key: childValue,
                    selectedOption,
                    index,
                    name,
                    onBlur,
                    onChange: changeHandler,
                    selectionMade: !!selectedOption,
                });
            }),
            errorMessage && (React.createElement(ErrorNotification, { id: errorId, errorMessage: errorMessage })))));
});
//# sourceMappingURL=RadioGroup.js.map